import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Reveal from "@/components/Reveal";
import useHeaderScroll from "@/lib/useHeaderScroll";
import { PRODUCT_HOME } from "@/lib/routes";
import LegalLinks from "@/components/LegalLinks";

const ICONS = ["/assets/socialpick-app.png", "/assets/bookitnow-app.png", "/assets/incontact-app.png"];
const PHOTOS = [
  ["/assets/socialdeal-1.jpg", "/assets/socialdeal-2.jpg", "/assets/socialdeal-3.jpg"],
  ["/assets/bookitnow-1.jpg", "/assets/bookitnow-2.jpg", "/assets/bookitnow-3.jpg"],
  ["/assets/incontact-1.jpg", "/assets/incontact-2.jpg", "/assets/incontact-3.jpg"],
];

export default function ProductPage({ index = 0 }) {
  const { t } = useLang();
  const c = t.commerce;
  const p = c.products[index];
  const { scrolled, hidden } = useHeaderScroll();

  return (
    <main className="grain bg-[#0a0a0a] text-[#f4f1eb] min-h-screen flex flex-col" data-testid={`product-page-${index}`}>
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-[transform,background-color,border-color,backdrop-filter] duration-300 ease-out ${
          hidden ? "-translate-y-full" : "translate-y-0"
        } ${scrolled ? "bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"}`}
        data-testid="product-nav"
      >
        <div className="mx-auto max-w-[1180px] pl-6 pr-4 md:px-8 py-4 flex items-center justify-between">
          <Link to={PRODUCT_HOME} data-testid="product-nav-home" className="flex items-center gap-3 group">
            <img src={ICONS[index]} alt={`Eagle Commerce ${p.name} app`} className="h-11 w-11 rounded-[22%] transition-transform duration-500 group-hover:rotate-[6deg]" />
            <span className="flex flex-col">
              <span className="font-gateway font-bold uppercase tracking-[0.14em] text-[0.95rem] leading-[1.02] transition-opacity group-hover:opacity-70">
                Eagle Commerce
              </span>
              <span className="font-mono-data text-[0.56rem] tracking-[0.32em] leading-[1.1] mt-[2px] text-brass">
                {p.name.toUpperCase()}
              </span>
            </span>
          </Link>
          <div className="absolute right-4 top-4 md:hidden"><LanguageSwitcher variant="dark" /></div>
        </div>
      </header>

      {/* Desktop: language button pinned to the top-right corner, same as the Real Estate landing */}
      <div className="hidden md:block fixed top-4 right-4 z-50">
        <LanguageSwitcher variant="dark" />
      </div>

      <section className="relative flex-1 pt-24 md:pt-28 pb-6 overflow-hidden" data-testid="product-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(181,149,96,0.13),transparent_55%)]" />
        <div className="relative mx-auto max-w-[1180px] px-6 md:px-8">
          <img
            src={ICONS[index]}
            alt=""
            data-testid="product-app-icon"
            className="pointer-events-none absolute right-6 md:right-8 top-0 lg:top-1/2 lg:-translate-y-1/2 w-16 h-16 lg:w-[11vw] lg:h-auto lg:max-w-[170px] rounded-[22%] animate-float-slow"
          />
          <Reveal>
            <div className="flex flex-wrap items-center gap-4 mb-6 pr-20 lg:pr-0">
              <span className="font-mono-data text-[0.66rem] tracking-[0.3em] text-brass uppercase" data-testid="product-kicker">
                Eagle Commerce {p.name}
              </span>
              <span
                className={`font-mono-data text-[0.56rem] tracking-[0.22em] px-3 py-1.5 rounded-full border ${
                  p.status === "live" ? "border-brass/60 text-brass" : "border-white/20 text-white/45"
                }`}
                data-testid="product-status"
              >
                {(p.status === "live" ? c.live : c.soon).toUpperCase()}
              </span>
            </div>
            <h1 className="font-gateway font-black leading-[0.95] tracking-[0.01em] text-[2.6rem] md:text-[4.2rem] max-w-[16ch]" data-testid="product-h1">
              {p.h1}
            </h1>
            <p className="mt-6 max-w-[54ch] font-body text-base md:text-lg leading-relaxed text-white/60" data-testid="product-story">
              {p.story}
            </p>
            {p.url ? (
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-visit"
                className="mt-8 inline-flex items-center gap-2 font-mono-data text-[0.66rem] tracking-[0.2em] bg-brass text-[#0a0a0a] px-6 py-4 hover:bg-[#f4f1eb] transition-colors"
              >
                {c.visit.toUpperCase()} {p.name.toUpperCase()}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            ) : (
              <span
                data-testid="product-soon"
                className="mt-8 inline-flex items-center gap-2 font-mono-data text-[0.62rem] tracking-[0.22em] px-5 py-3.5 rounded-full border border-white/20 text-white/45"
              >
                {c.soon.toUpperCase()}
              </span>
            )}
          </Reveal>
        </div>
      </section>

      <section className="pb-10" data-testid="product-photos">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {PHOTOS[index].map((src, i) => (
              <Reveal key={src} delay={i * 0.08}>
                <img
                  src={src}
                  alt={`${p.name} preview ${i + 1}`}
                  data-testid={`product-photo-${i}`}
                  className="w-full aspect-[16/9] object-cover rounded-[1rem] border border-white/10"
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-6 mt-auto" data-testid="product-footer">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8 flex flex-wrap items-end justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-4">
          <div>
            <p className="font-gateway font-bold tracking-[0.24em] text-[0.72rem]">{p.brand.toUpperCase()}</p>
            <p className="font-mono-data text-[0.58rem] tracking-[0.18em] text-white/40 mt-1.5">{t.common.company}</p>
          </div>
          <LegalLinks className="font-mono-data text-[0.58rem] tracking-[0.18em] text-white/40 order-last w-full text-center lg:order-none lg:w-auto" />
          <span className="lg:justify-self-end font-mono-data text-[0.6rem] tracking-[0.22em] text-white/40">© {new Date().getFullYear()} {p.brand}</span>
        </div>
      </footer>
    </main>
  );
}
